import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import type { INavItemBadgeType } from "@similarweb/ui-components";
import { NavItemBadge, Tab, TabList, TabPanel, Tabs, UseCaseHomepage } from "@similarweb/ui-components";
import { Box, Flex, Stack } from "@similarweb/ui-components/foundations";

import { resolveAdIntelRouteForCurrentSolution } from "app/common/utils/resolveAdIntelRouteForCurrentSolution";
import { ScrollContainer } from "app/components/layout/ScrollContainer";
import { BADGE_ITEMS } from "app/components/sidebar3/types";
import { translate } from "app/filters/i18nFilter";
import { ROUTES as socialChannelsRoutes } from "app/pages/digital-marketing/ad-intelligence/app/social-channels/constants";
import { KeywordsWebsitesRecentsWidget } from "app/pages/digital-suite/home-page/components/RecentsWidget/KeywordsWebsitesRecentsWidget";
import { allTrackers } from "app/services/track/track";
import { SecondaryHomePageHeaderImageUrl } from "app/UtilitiesAndConstants/Constants/Images";
import { suggestedEntities } from "pro-features/components/AutocompleteAdIntelligence/AutocompleteAdIntelligenceHelper";
import { AutocompleteDigitalMarketing } from "pro-features/components/AutocompleteDigitalMarketing/AutocompleteDigitalMarketing";
import { ROUTES as advertiserActivityRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/advertiserActivity/advertiserActivityConstants";
import { ROUTES as displayAdvertisingRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingConstants";
import { ROUTES as searchAdvertisingRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/searchAdvertising/searchAdvertisingConstants";

import { hasOnlySearchAvailable } from "../common/utils/utils";

const FILTER_RECENTS_BY = [
    "adintelligence-advertiseractivity.root.frame",
    "adintelligence-displayadvertising.root.frame",
    "adintelligence-searchadvertising.root.frame",
    "adintelligence-searchadvertising.root.pageFrame",
    "adintelligence-socialchannels.root.frame",
];

const AutocompleteWrapper = styled.div`
    width: 100%;

    & div.AutoCompleteContainer {
        z-index: 5;
    }
`;

const suggestions = suggestedEntities.map((entity) => entity.name);

interface ITabConfig {
    translationKey: string;
    targetState?: string;
    excludeCompareRecents?: boolean;
    isDisabled?: boolean;
    trackingName?: string;
    badge?: INavItemBadgeType;
}

const getAdvertiserActivityTabs = (): ITabConfig[] => [
    {
        trackingName: "All Channels",
        translationKey: "advertiser.activity.home.page.tab.overview",
        targetState: advertiserActivityRoutes.overview,
        excludeCompareRecents: true,
    },
    {
        trackingName: "Display",
        translationKey: "advertiser.activity.home.page.tab.display",
        targetState: displayAdvertisingRoutes.overview.stateName,
    },
    {
        trackingName: "Social",
        translationKey: "advertiser.activity.home.page.tab.social",
        targetState: socialChannelsRoutes.ADS.stateName,
        badge: BADGE_ITEMS.NEW,
    },
    {
        trackingName: "Search",
        translationKey: "advertiser.activity.home.page.tab.search",
        targetState: searchAdvertisingRoutes.landingPages,
    },
];

const useHomepageTabs = (): {
    selectedTab: string;
    setSelectedTab: (tab: string) => void;
    advertiserActivityTabs: ITabConfig[];
} => {
    const [selectedTab, setSelectedTab] = useState<string>(
        hasOnlySearchAvailable()
            ? searchAdvertisingRoutes.landingPages
            : (getAdvertiserActivityTabs()[0].targetState ?? ""),
    );

    const searchTab = getAdvertiserActivityTabs().find(
        (tab) => tab.targetState === searchAdvertisingRoutes.landingPages,
    );
    const advertiserActivityTabs = hasOnlySearchAvailable() && searchTab ? [searchTab] : getAdvertiserActivityTabs();

    return { selectedTab, setSelectedTab, advertiserActivityTabs };
};

const AdvertiserActivityHomePage = () => {
    const { state } = useCurrentStateAndParams();
    const { selectedTab, setSelectedTab, advertiserActivityTabs } = useHomepageTabs();

    // Tab identity stays on WI state names; only the name handed to navigation consumers is
    // resolved, so a SI mount links to its own registered routes instead of WI future states.
    const targetState = resolveAdIntelRouteForCurrentSolution(
        advertiserActivityTabs.find((tab) => tab.targetState === selectedTab)?.targetState,
        state.name,
    );
    const selectedIndex = Math.max(
        0,
        advertiserActivityTabs.findIndex((tab) => tab.targetState === selectedTab),
    );

    const trackItemClick = () => {
        const tabToTrack = advertiserActivityTabs.find((tab) => tab.targetState === selectedTab);
        if (tabToTrack) {
            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `Advertiser Activity HomePage/Filter Channels/${tabToTrack.trackingName}`,
            );
        }
    };

    return (
        <ScrollContainer>
            <UseCaseHomepage
                title={translate(state.pageTitle as string)}
                titlePosition="left-aligned"
                subtitlePosition="left-aligned"
                subtitle={translate("ad.intelligence.advertiser.activity.analysis.homepage.subtitle")}
                headerImageUrl={SecondaryHomePageHeaderImageUrl}
                aboveSearchContent={
                    advertiserActivityTabs.length === 1 ? undefined : (
                        <Stack
                            background={colorsPalettes.carbon[0]}
                            borderRadius="6px"
                        >
                            <Tabs
                                selectedIndex={selectedIndex}
                                onSelect={(index: number) =>
                                    setSelectedTab(advertiserActivityTabs[index]?.targetState ?? "")
                                }
                            >
                                <TabList>
                                    {advertiserActivityTabs.map((tab) => (
                                        <Tab
                                            key={tab.trackingName}
                                            disabled={tab.isDisabled}
                                        >
                                            <Flex
                                                alignItems="center"
                                                gap="6px"
                                            >
                                                {translate(tab.translationKey)}
                                                {tab.badge ? (
                                                    <NavItemBadge
                                                        type={tab.badge}
                                                        text={translate("sidenav.new")}
                                                    />
                                                ) : null}
                                            </Flex>
                                        </Tab>
                                    ))}
                                </TabList>
                                {advertiserActivityTabs.map((tab) => (
                                    <TabPanel key={tab.trackingName} />
                                ))}
                            </Tabs>
                            <Box padding="24px">
                                <AutocompleteWrapper>
                                    <AutocompleteDigitalMarketing
                                        includeKeywordsData={false}
                                        maxCompareItemsToDisplay={1}
                                        websiteState={targetState}
                                        onItemClick={trackItemClick}
                                        suggestedEntities={suggestions}
                                    />
                                </AutocompleteWrapper>
                            </Box>
                        </Stack>
                    )
                }
                listItems={[
                    <KeywordsWebsitesRecentsWidget
                        key="recents"
                        withoutKeywords
                        suggestedEntities={suggestions}
                        filterRecentsBy={FILTER_RECENTS_BY}
                        suggestionsTargetState={targetState}
                    />,
                ]}
            />
        </ScrollContainer>
    );
};

export default AdvertiserActivityHomePage;
